import React, { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import dayjs from 'dayjs'

import {
  LntButton,
  LntForm,
  LntFormItem,
  LntFormGroup,
  LntSelect,
  LntDatePicker,
  LntInputNumber
} from '@app/components/base'

import { MONITORINGS_TYPES_KEY, MONITORINGS_SUBTYPES_KEY, LOCATION_KEY } from '@app/constants/queryKeys'
import { BUTTON_TYPE, dateFormat, SIZE } from '@app/constants'
import { FILTERS_PLACEHOLDERS } from '@app/constants/filters'
import { DAY_OF_WEEK_OPTIONS } from '@app/constants/week'

import { getTypes, getSubtypesTypes } from '@app/api/routes/monitorings'
import { getStores } from '@app/api/routes/locationStores'
import { getClusters } from '@app/api/routes/locationСlusters'
import { getClusterGroups } from '@app/api/routes/locationClusterGroups'

import { getFilterFromEntities } from '@app/utils/entitiesAdapters'

import { isEmptyArray } from '@app/utils'

import { SEPARATOR } from '@app/constants/separator'

import styles from './styles.module.scss'

import type { CommonMonitoringScheduleDrawerProps } from '../types'
import type { LntSelectProps } from '@app/components/base/Select/types'
import type { LntDatePickerProps } from '@app/components/base/Form/Input/DatePicker/types'
import type { NewMonitoringScheduleData } from '@app/pages/MonitoringSchedulePage/types'

const commonSelectProps: LntSelectProps = {
  showArrow: true,
  mode: 'multiple',
  size: SIZE.middle,
  strictValue: true,
  tokenSeparators: [SEPARATOR.newline]
}

const commonDatePickerProps: LntDatePickerProps = {
  picker: 'time',
  size: SIZE.middle
}

export const CommonMonitoringScheduleForm: React.FC<CommonMonitoringScheduleDrawerProps> = ({
  data,
  onSubmit,
  onClose
}) => {
  const formInstance = useForm<NewMonitoringScheduleData>()
  const isValidForm = formInstance.formState.isValid
  const isEdit = !!data

  const hasMonitoringTypesIds = useMemo<boolean>(
    () => !isEmptyArray(formInstance.watch('monitoringTypeIds')),
    [formInstance.watch('monitoringTypeIds')]
  )

  const hasMonitoringSubtypeIds = useMemo<boolean>(
    () => !isEmptyArray(formInstance.watch('monitoringSubtypeIds')),
    [formInstance.watch('monitoringSubtypeIds')]
  )

  return (
    <LntForm<NewMonitoringScheduleData>
      layout='vertical'
      onSubmit={ onSubmit }
      formInstance={ formInstance }
    >
      <div className={ styles.commonMonitoringScheduleForm__itemsWrapper }>
        <LntFormItem
          name='monitoringTypeIds'
          defaultValue={ isEdit ? [data?.monitoringType] : [] }
        >
          <LntSelect
            { ...commonSelectProps }
            placeholder={ FILTERS_PLACEHOLDERS.monitoringsTypeFilter }
            querySelect={ {
              queryKey: [MONITORINGS_TYPES_KEY.typesItems],
              queryFn: getTypes,
              select: getFilterFromEntities
            } }
            disabled={ isEdit || hasMonitoringSubtypeIds }
          />
        </LntFormItem>

        <LntFormItem
          name='monitoringSubtypeIds'
          defaultValue={ isEdit ? [data?.monitoringSubtype] : [] }
        >
          <LntSelect
            { ...commonSelectProps }
            placeholder={ FILTERS_PLACEHOLDERS.monitoringsSubtypesFilter }
            querySelect={ {
              queryKey: [MONITORINGS_SUBTYPES_KEY.subtypesItems],
              queryFn: getSubtypesTypes,
              select: getFilterFromEntities
            } }
            disabled={ isEdit || hasMonitoringTypesIds }
          />
        </LntFormItem>

        <LntFormItem
          name='storeIds'
          defaultValue={ isEdit ? [data?.store] : [] }
        >
          <LntSelect
            { ...commonSelectProps }
            placeholder={ FILTERS_PLACEHOLDERS.storesFilter }
            querySelect={ {
              queryKey: [LOCATION_KEY.locationStoresItems],
              queryFn: getStores,
              select: getFilterFromEntities
            } }
            disabled={ isEdit }
          />
        </LntFormItem>

        <LntFormItem
          name='clusterIds'
          defaultValue={ isEdit ? [data?.cluster] : [] }
        >
          <LntSelect
            { ...commonSelectProps }
            placeholder={ FILTERS_PLACEHOLDERS.clustersFilter }
            querySelect={ {
              queryKey: [LOCATION_KEY.locationClustersItems],
              queryFn: getClusters,
              select: getFilterFromEntities
            } }
            disabled={ isEdit }
          />
        </LntFormItem>

        <LntFormItem
          name='clustersGroupIds'
          defaultValue={ isEdit ? [data?.clustersGroup] : [] }
        >
          <LntSelect
            { ...commonSelectProps }
            placeholder={ FILTERS_PLACEHOLDERS.clusterGroupsFilter }
            querySelect={ {
              queryKey: [LOCATION_KEY.locationClusterGroupsItems],
              queryFn: getClusterGroups,
              select: getFilterFromEntities
            } }
            disabled={ isEdit }
          />
        </LntFormItem>

        <LntFormGroup label='Дата и время запуска мониторинга'>
          <div className={ styles.commonMonitoringScheduleForm__item }>
            <LntFormItem
              name='dateOfStartMonitoring'
              defaultValue={ isEdit ? data.monitoringStartDayOfWeek : 1 }
              validationOptions={ { rules: { required: true } } }
              className={ styles.commonMonitoringScheduleForm__itemSize }
            >
              <LntSelect
                placeholder={ FILTERS_PLACEHOLDERS.daysOfWeek }
                options={ DAY_OF_WEEK_OPTIONS }
                showArrow
                size={ SIZE.middle }
              />
            </LntFormItem>

            <LntFormItem
              name='timeOfStartMonitoring'
              defaultValue={
                isEdit ? dayjs(data?.monitoringStartAtTime, dateFormat.HH_mm) : dayjs('00:01', dateFormat.HH_mm)
              }
              validationOptions={ { rules: { required: true } } }
            >
              <LntDatePicker
                { ...commonDatePickerProps }
                placeholder={ FILTERS_PLACEHOLDERS.setTime }
                format={ dateFormat.HH_mm }
                className={ styles.commonMonitoringScheduleForm__itemSize }
              />
            </LntFormItem>
          </div>
        </LntFormGroup>

        <LntFormGroup label='Получение данных'>
          <div className={ styles.commonMonitoringScheduleForm__item }>
            <LntFormItem
              name='dateOfReceiveData'
              defaultValue={ isEdit ? data.receivingDataDeadlineDays : '' }
              validationOptions={ { rules: { required: true } } }
            >
              <LntInputNumber
                size={ SIZE.middle }
                placeholder='+0 дней'
                className={ styles.commonMonitoringScheduleForm__itemSize }
              />
            </LntFormItem>

            <LntFormItem
              name='timeOfReceiveData'
              defaultValue={ isEdit ? dayjs(data.receivingDataDeadlineTime, dateFormat.HH_mm) : '' }
              validationOptions={ { rules: { required: true } } }
            >
              <LntDatePicker
                { ...commonDatePickerProps }
                placeholder={ FILTERS_PLACEHOLDERS.setTime }
                format={ dateFormat.HH_mm }
                className={ styles.commonMonitoringScheduleForm__itemSize }
              />
            </LntFormItem>
          </div>
        </LntFormGroup>

        <LntFormGroup label='Обработка данных'>
          <div className={ styles.commonMonitoringScheduleForm__item }>
            <LntFormItem
              name='dateOfProcessingData'
              defaultValue={ isEdit ? data.processingDataDeadlineDays : '' }
              validationOptions={ { rules: { required: true } } }
            >
              <LntInputNumber
                size={ SIZE.middle }
                placeholder='+0 дней'
                className={ styles.commonMonitoringScheduleForm__itemSize }
              />
            </LntFormItem>

            <LntFormItem
              name='timeOfProcessingData'
              defaultValue={ isEdit ? dayjs(data.processingDataDeadlineTime, dateFormat.HH_mm) : '' }
              validationOptions={ { rules: { required: true } } }
            >
              <LntDatePicker
                { ...commonDatePickerProps }
                placeholder={ FILTERS_PLACEHOLDERS.setTime }
                format={ dateFormat.HH_mm }
                className={ styles.commonMonitoringScheduleForm__itemSize }
              />
            </LntFormItem>
          </div>
        </LntFormGroup>

        <LntFormGroup label='Выставление новой цены'>
          <div className={ styles.commonMonitoringScheduleForm__item }>
            <LntFormItem
              name='dateOfSettingNewPriceData'
              defaultValue={ isEdit ? data.newDataDeadlineDays : '' }
              validationOptions={ { rules: { required: true } } }
            >
              <LntInputNumber
                size={ SIZE.middle }
                placeholder='+0 дней'
                className={ styles.commonMonitoringScheduleForm__itemSize }
              />
            </LntFormItem>

            <LntFormItem
              name='timeOfSettingNewPriceData'
              defaultValue={ isEdit ? dayjs(data.newDataDeadlineTime, dateFormat.HH_mm) : '' }
              validationOptions={ { rules: { required: true } } }
            >
              <LntDatePicker
                { ...commonDatePickerProps }
                placeholder={ FILTERS_PLACEHOLDERS.setTime }
                format={ dateFormat.HH_mm }
                className={ styles.commonMonitoringScheduleForm__itemSize }
              />
            </LntFormItem>
          </div>
        </LntFormGroup>

        <div className={ styles.commonMonitoringScheduleForm__buttons }>
          <LntButton
            type={ BUTTON_TYPE.secondary }
            size={ SIZE.middle }
            onClick={ onClose }
          >
            Отменить
          </LntButton>
          <LntButton
            type={ BUTTON_TYPE.primary }
            size={ SIZE.middle }
            htmlType='submit'
            disabled={ !isValidForm }
          >
            Сохранить
          </LntButton>
        </div>
      </div>
    </LntForm>
  )
}
